<template>
  <div class="d-flex flex-column bookmark-new-container">
    <HeaderLeftContainer :title="$t(`common.actions.addBookmark`)" :back-to="{ name: 'default' }" class="flex-grow-0"></HeaderLeftContainer>

    <v-container fluid class="flex-grow-1 overflow">
      <v-row>
        <v-col>
          <p class="text-primary">{{ $t('views.bookmarks.addTo') }}</p>
          <v-radio-group v-model="global" color="primary" hide-details>
            <v-radio :label="$t('views.bookmarks.myBookmarks')" :value="false"></v-radio>
            <v-radio :label="$t('views.bookmarks.globalBookmarks')" :value="true"></v-radio>
          </v-radio-group>
        </v-col>
      </v-row>

      <v-row no-gutters>
        <v-col>
          <ZColorIconPicker
            v-model="selectedIcon"
            v-model:color="selectedColor"
            class="custom_icon_selector"
            show-preview
            show-color-picker></ZColorIconPicker>
        </v-col>
      </v-row>

      <ZLocationInput
        v-model:latitude="markerCoordinates.latitude"
        v-model:longitude="markerCoordinates.longitude"
        :geocoder-key="googleKey"
        @geocoder-result="geocoderResult = $event">
        <v-row>
          <v-col>
            <ZLocationField
              v-model="search"
              :label="$t('common.actions.search')"
              variant="solo-filled"
              density="compact"
              flat
              rounded
              hide-details
              clearable
              menu-icon=""
              append-inner-icon="mdi-target" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" class="pl-6">
            <p class="bodyTexts opacity67">{{ $t('domain.fields.latitude') }}</p>
            <ZLatitudeField>
              <template #default="{ value, update, type, mask }">
                <input
                  :key="type"
                  v-z-input-mask="mask"
                  class="customInput"
                  :value="value ? Number(value).toFixed(4) : ' - '"
                  @input="update($event as InputEvent)" />
              </template>
            </ZLatitudeField>
          </v-col>
          <v-col cols="6" class="">
            <p class="bodyTexts opacity67">{{ $t('domain.fields.longitude') }}</p>
            <ZLongitudeField>
              <template #default="{ value, update, type, mask }">
                <input
                  :key="type"
                  v-z-input-mask="mask"
                  class="customInput"
                  :value="value ? Number(value).toFixed(4) : ' - '"
                  @input="update($event as InputEvent)" />
              </template>
            </ZLongitudeField>
          </v-col>
        </v-row>
      </ZLocationInput>

      <v-row>
        <v-col cols="12" class="mt-2 bodyTexts">
          <p>{{ $t('views.bookmarks.details') }}</p>
        </v-col>
        <v-col cols="12 px-3">
          <v-text-field
            v-model="nameField"
            variant="outlined"
            :label="$t('domain.fields.name')"
            density="compact"
            hide-details
            heigth="100px"></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12  px-3">
          <v-text-field
            v-model="commentField"
            variant="outlined"
            :label="$t('domain.fields.comment')"
            density="compact"
            hide-details
            heigth="100px"></v-text-field>
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid class="action-buttons">
      <v-row class="">
        <v-col>
          <v-btn class="pa-1" block color="rgb(var(--v-theme-background))" variant="flat" @click="closeAddBookMark">
            {{ $t('common.actions.cancel') }}
          </v-btn>
        </v-col>
        <v-col>
          <v-btn class="pa-1" block color="rgb(var(--v-theme-primary))" variant="flat" @click="submit()">
            {{ $t('common.actions.save') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script lang="ts" setup>
// Framework import
import { ref, onMounted, onUnmounted } from 'vue'
import { watch } from 'vue'

import { useRouter } from 'vue-router'

import { storeToRefs } from 'pinia'

// Components import
import useView from '@tsyl/karonte/lib/composables/useView'
import type { Coordinates, RGeocoderResult } from '@tsyl/radon/lib/types/geocoder'

import HeaderLeftContainer from '@/components/HeaderLeftContainer.vue'

// Types import

// Store import

// Radon import

// Composable import
import bookmarksComposable from '@/composables/bookmarksComposable'

//Karonte import

import { saveBookmark } from '@/services/api/bookmarks'
import AuthStore from '@/stores/auth'
import MarkerStore from '@/stores/markerStore'
import type { Bookmark } from '@/types/Bookmark'

const auth = AuthStore()
const { user } = storeToRefs(auth)

const { getReactiveView } = useView()
const googleKey = import.meta.env.VITE_GOOGLE_API_KEY
const router = useRouter()

const { addBookmark } = bookmarksComposable()

const selectedIcon = ref<string>()
const selectedColor = ref<string>('#c9c9c9') // gray by default

const nameField = ref('' as string)
const commentField = ref('' as string)

const view = getReactiveView()

const clickMarker = ref<boolean>(false)
const markerStore = MarkerStore()
const { markerCoordinates, enableMarker, zoom } = storeToRefs(markerStore)
enableMarker.value = true

const search = ref({ name: '' } as RGeocoderResult)
const geocoderResult = ref({} as RGeocoderResult)

const global = ref(false)

watch(search, (val) => {
  if (!val) markerCoordinates.value = {}
  clickMarker.value = false
})

watch(geocoderResult, (val) => {
  if (val && !clickMarker.value) goToExtent()
  search.value = val
})

function closeAddBookMark() {
  enableMarker.value = false
  router.push({ name: 'bookmarks' })
}

async function submit() {
  const book: Bookmark = {
    name: nameField.value,
    y: markerCoordinates.value?.y,
    x: markerCoordinates.value?.x,
    global: global.value,
    user: global.value ? undefined : user.value?.id,
    icon: selectedIcon.value,
    color: selectedColor.value,
    comment: commentField.value,
    zoom: zoom.value
  }
  addBookmark(book)
  await saveBookmark(book)
  closeAddBookMark()
}

async function mapClickCallback() {
  clickMarker.value = true
}

function goToExtent() {
  const coordinates: Coordinates = {
    latitude: markerCoordinates.value?.latitude as number,
    longitude: markerCoordinates.value?.longitude as number
  }
  view.value?.goTo(coordinates, 15)
}

onMounted(() => {
  view.value?.addCallback('click', mapClickCallback)
})

onUnmounted(() => {
  view.value?.removeCallback('click', mapClickCallback)
  enableMarker.value = false
})
</script>

<style scoped lang="scss">
.overflow {
  overflow-y: auto;
}

.customInput {
  max-width: 100%;
}
.bookmark-new-container {
  height: 100vh;
}

:deep(.mdi-close-circle) {
  margin-right: 8px;
}
:deep(.mdi-target) {
  padding-right: 14px;
}

.action-buttons {
  box-shadow: 0px -1px 0px rgb(var(--v-theme-background));
}

:deep(.custom_icon_selector .v-row .v-col .v-card) {
  background-color: rgb(var(--v-theme-background)) !important;
}
:deep(.v-pagination__list) {
  transform: scale(0.68);
}
</style>
